import Query from '../../Query.js';
import {
	ClientUserDetailReturnFields,
	ContactScalarFields,
	OrgUserDetailReturnFields,
} from '../returnFields';

const ContactDetailPageReturnFields = [
	...ContactScalarFields,
	{
		customer: [
			'id',
			'email',
			{ guestUser: [ 'id' ] },
			{ clientUser: ClientUserDetailReturnFields },
			{ orgUser: OrgUserDetailReturnFields },
		],
	},
	{ assignedMember: OrgUserDetailReturnFields },
	{ vendor: [ 'id' ] },
];

// Begin contact section

/**
 * Gets a contact between a given organization and client user.
 *
 * @param { String } - clientID
 * @param { String } - orgID
 *
 * @returns { Promise<{ contact: Record<string, any> } | { errors: import('graphql').GraphQLError[] }> }
 */
const getContact = async function( clientID, orgID ) {
	if ( !clientID || !orgID ) {
		const errorMessage = `Could not get contact. Arguments not received: ${
			!clientID && 'clientID'
		} ${!orgID && 'orgID'}`; //eslint-disable-line
		return { errors: [ { message: errorMessage } ] };
	}

	const { data, errors } = await this.request(
		new Query( {
			type: 'query',
			name: 'getOrganizationWhere',
			params: {
				where: {
					id: orgID,
				},
			},
			returnFields: [
				new Query( {
					name: 'contacts',
					params: { where: { customer: { clientUser: { id: clientID } } } },
					returnFields: [
						...ContactScalarFields,
						{ assignedMember: OrgUserDetailReturnFields },
						{ vendor: [ 'id' ] },
						{
							customer: [
								'id',
								'userType',
								'id',
								{
									clientUser: [
										'weddingDate',
										'id',
										'plannerOrgHasDocumentsPermissions',
										{ assignedPlanner: [ 'id', { organization: [ 'id' ] } ] },
									],
								},
							],
						},
					],
				} ),
			],
		} )
	);
	if ( errors ) {
		return { errors };
	}
	const { contacts } = data.data.getOrganizationWhere;
	return {
		contact:
			Array.isArray( contacts ) && contacts.length ? contacts[ 0 ] : undefined,
	};
};

/**
 * Wrapper for createContactAsClient.
 * allows a client to connect to an org,
 * or for their planner for connect to an org on their behalf.
 *
 * @param {String} userID - ID of the User (not ClientUser) on the contact.
 * @param {String} orgID - ID of the org on the contact.
 *
 * @returns {Promise<{ contact: Record<string, any> } | { errors: string[] }} res { errors, contact }
 */
const createContactAsClient = async function( userID, orgID ) {
	const { data, errors } = await this.request(
		new Query( {
			type: 'mutation',
			name: 'createContactAsClient',
			params: {
				where: { id: userID },
				data: { id: orgID },
			},
			returnFields: [
				...ContactScalarFields,
				{
					customer: [
						'id',
						'email',
						{ guestUser: [ 'id' ] },
						{ clientUser: ClientUserDetailReturnFields },
					],
				},
				{ vendor: [ 'id' ] },
				{ assignedMember: [ 'id' ] },
				{ invitation: [ 'recipientFirstName', 'recipientLastName' ] },
			],
		} )
	);
	if ( errors ) return { errors };

	const contact = data.data.createContactAsClient;

	return {
		contact,
	};
};

/**
 * Request a contact be made active by another party.
 *
 * @param {String} clientID - ID of the client on the contact.
 * @param {String} orgID - ID of the org on the contact.
 *
 * @returns {Promise<{ errors: string[] } | { contact: object }>} res { errors, contact }
 */
const requestContactActivation = async function( contactID ) {
	const { data, errors } = await this.request(
		new Query( {
			type: 'mutation',
			name: 'requestContactActivation',
			params: {
				where: { id: contactID },
			},
			returnFields: [
				// eslint-disable-next-line array-element-newline
				...ContactDetailPageReturnFields,
				{ invitation: [ 'recipientFirstName', 'recipientLastName' ] },
			],
		} )
	);
	if ( errors ) return { errors };

	const contact = data.data.requestContactActivation;

	return { contact };
};

/**
 * allows a client, client's planner, or vendor to accept a contact and set it active
 *
 * @param { String } contactID the id of the contact to be accepted
 * @param { boolean } [sendEmails]
 * @returns {Promise<{ contact: Record<string, any> } | { errors: string[] }}
 */
const activateContact = async function( contactID, sendEmails = true ) {
	const { data, errors } = await this.request(
		new Query( {
			type: 'mutation',
			name: 'activateContact',
			params: {
				where: { id: contactID },
				data: { sendEmails },
			},
			returnFields: [
				...ContactScalarFields,
				{ vendor: [ 'id' ] },
				{ assignedMember: [ 'id' ] },
				{
					customer: [
						'id',
						'email',
						{ guestUser: [ 'id' ] },
						{ clientUser: ClientUserDetailReturnFields },
					],
				},
				{ invitation: [ 'recipientFirstName', 'recipientLastName' ] },
			],
		} )
	);
	if ( errors ) return { errors };

	const contact = data.data.activateContact;

	return {
		contact,
	};
};

/**
 * Declines a contact request.
 * Should be operable by the vendor, client, or client's planner.
 *
 * @param {String} contactId - ID of the contact to decline.
 *
 * @returns {Promise<{ success: true, contact: Record<string, any> } | { errors: import('graphql').GraphQLError[] }>} res - { errors, success }
 */
const declineContact = async function( contactId ) {
	const { data, errors } = await this.request(
		new Query( {
			type: 'mutation',
			name: 'declineContact',
			params: {
				where: { id: contactId },
			},
			returnFields: ContactScalarFields,
		} )
	);
	if ( errors || data.errors ) return { errors: errors || data.errors };

	const contact = data.data.declineContact;

	return {
		success: true,
		contact,
	};
};

/**
 * Mark a contact as inactive.
 *
 * @param {String} contactId - ID of the contact to cancel.
 *
 * @returns {Promise<{ success: true, contact: Record<string, any> } | { errors: import('graphql').GraphQLError[] }>} res - { errors, success }
 */
const cancelContact = async function( contactId ) {
	const { data, errors } = await this.request(
		new Query( {
			type: 'mutation',
			name: 'cancelContact',
			params: {
				where: { id: contactId },
			},
			returnFields: ContactDetailPageReturnFields,
		} )
	);
	if ( errors || data.errors ) return { errors: errors || data.errors };

	const contact = data.data.cancelContact;

	return {
		success: true,
		contact,
	};
};

export {
	activateContact,
	createContactAsClient,
	cancelContact,
	declineContact,
	getContact,
	requestContactActivation,
};
